<script>
import { mapGetters } from 'vuex'
import Request from '@src/mixins/request'
import RatePlanForm from '@src/forms/RatePlan'
import dropdown from '@src/data/dropdown.json'

export default {
  metaInfo() {
    return {
      title: 'Rate Plan Detail',
      meta: [{ name: 'description', content: 'Rate Plan Detail' }],
    }
  },

  $_veeValidate: { validator: 'new' },

  mixins: [Request],

  props: {
    roomTypes: {
      type: Array,
      required: true,
    },
    cancellations: {
      type: Array,
      required: true,
    },
    item: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      dropdown,
      form: new RatePlanForm(),
      color: 'gold',
      schema: RatePlanForm.validator(),
      benefit: '',
    }
  },

  computed: {
    ...mapGetters('app', ['propertyId']),
    isNew() {
      return this.$route.params.ratePlanId === 'new'
    },
    text() {
      return {
        title: this.isNew ? 'New Rate Plan' : 'Edit Rate Plan',
        submit: this.isNew ? 'Save' : 'Update',
      }
    },
  },

  mounted() {
    if (!this.isNew) {
      this.form = new RatePlanForm(this.item)
    }
  },

  methods: {
    onSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.isNew) return this.addItem()

          return this.updateItem()
        }
      })
    },
    addItem() {
      this.updateData({
        url: this.endpoint.ratePlan.add,
        data: {
          propertyId: this.propertyId,
          data: this.form,
        },
      }).then(() => {
        this.backToList()
      })
    },
    updateItem() {
      this.updateData({
        url: this.endpoint.ratePlan.update,
        data: {
          id: this.item.id,
          propertyId: this.propertyId,
          update: this.form,
        },
      }).then(() => {
        this.backToList()
      })
    },
    backToList() {
      this.$router.push({ name: 'rate-plan' })
    },
    addBenefit() {
      if (this.benefit) {
        this.form.benefits.push(this.benefit)
        this.benefit = ''
      }
    },
    removeBenefit(index) {
      this.form.benefits.splice(index, 1)
    },
  },
}
</script>

<template>
  <VForm @submit.prevent="onSubmit">
    <BaseView :title="text.title" :color="color">
      <VLayout row wrap>
        <!-- Basic Info -->
        <VFlex class="mb-4" xs12>
          <div class="title mb-2">Basic Info</div>

          <BaseControlInput label="Rate plan name">
            <VTextField
              v-model="form.name"
              v-validate="schema.name"
              data-vv-name="rate plan name"
              label="Rate plan name"
              :error-messages="errors.collect('rate plan name')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Basic Info -->

        <!-- Conditions -->
        <VFlex class="mb-4" xs12 sm6>
          <div class="title mb-2">Conditions</div>

          <BaseControlInput label="Selling from">
            <BaseDatePicker
              v-model="form.sellStartDate"
              v-validate="schema.sellStartDate"
              data-vv-name="selling from"
              label="Selling from"
              :error-messages="errors.collect('selling from')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Selling to">
            <BaseDatePicker
              v-model="form.sellEndDate"
              v-validate="schema.sellEndDate"
              data-vv-name="selling to"
              label="Selling to"
              :error-messages="errors.collect('selling to')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Selling time from">
            <BaseTimePicker
              v-model="form.sellStartTime"
              v-validate="schema.sellStartTime"
              data-vv-name="selling time from"
              label="Selling time from"
              :error-messages="errors.collect('selling time from')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Selling time to">
            <BaseTimePicker
              v-model="form.sellEndTime"
              v-validate="schema.sellEndTime"
              data-vv-name="selling time to"
              label="Selling time to"
              :error-messages="errors.collect('selling time to')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Stay from">
            <BaseDatePicker
              v-model="form.stayStartDate"
              v-validate="schema.stayStartDate"
              data-vv-name="stay from"
              label="Stay from"
              :error-messages="errors.collect('stay from')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Stay to">
            <BaseDatePicker
              v-model="form.stayEndDate"
              v-validate="schema.stayEndDate"
              data-vv-name="stay to"
              label="Stay to"
              :error-messages="errors.collect('stay to')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Rate increase / decrease">
            <VAutocomplete
              v-model="form.rateIncrease"
              v-validate="schema.rateIncrease"
              data-vv-name="rate increase or decrease"
              label="Rate increase / decrease"
              :error-messages="errors.collect('rate increase or decrease')"
              :color="color"
              :items="dropdown.ratePlanRate"
            />
          </BaseControlInput>

          <BaseControlInput label="Rate increase / decrease by">
            <VTextField
              v-model="form.rateAmount"
              v-validate="schema.rateAmount"
              data-vv-name="rate increase or decrease by"
              label="Rate increase / decrease by"
              prefix="USD"
              :error-messages="errors.collect('rate increase or decrease by')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Static rate">
            <VTextField
              v-model="form.staticRate"
              v-validate="schema.staticRate"
              data-vv-name="static rate"
              label="Static rate"
              prefix="USD"
              :error-messages="errors.collect('static rate')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Conditions -->

        <!-- Restrictions -->
        <VFlex class="mb-4" xs12 sm6>
          <div class="title mb-2">Restrictions</div>

          <BaseControlInput label="Breakfast included">
            <VAutocomplete
              v-model="form.breakfastIncluded"
              v-validate="schema.breakfastIncluded"
              data-vv-name="breakfast included"
              label="Breakfast included"
              :error-messages="errors.collect('breakfast included')"
              :color="color"
              :items="dropdown.switch"
            />
          </BaseControlInput>

          <BaseControlInput label="Extrabed allowed">
            <VAutocomplete
              v-model="form.extrabedAllowed"
              v-validate="schema.extrabedAllowed"
              data-vv-name="extrabed allowed"
              label="Extrabed allowed"
              :error-messages="errors.collect('extrabed allowed')"
              :color="color"
              :items="dropdown.switch"
            />
          </BaseControlInput>

          <BaseControlInput label="Room types">
            <VSelect
              v-model="form.roomTypes"
              v-validate="schema.roomTypes"
              data-vv-name="room types"
              label="Room types"
              :error-messages="errors.collect('room types')"
              :color="color"
              :items="roomTypes"
              item-text="name"
              item-value="id"
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text caption">
                  {{ form.roomTypes.length }} selected
                </span>
              </template>
            </VSelect>
          </BaseControlInput>

          <BaseControlInput label="Cancellation">
            <VAutocomplete
              v-model="form.cancellationId"
              v-validate="schema.cancellationId"
              data-vv-name="cancellation"
              label="Cancellation"
              :error-messages="errors.collect('cancellation')"
              :color="color"
              :items="cancellations"
              item-text="name"
              item-value="id"
            />
          </BaseControlInput>

          <BaseControlInput label="Sell limit per day">
            <VTextField
              v-model="form.sellLimitPerDay"
              v-validate="schema.sellLimitPerDay"
              data-vv-name="sell limit per day"
              label="Sell limit per day"
              :error-messages="errors.collect('sell limit per day')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Minimal LOS">
            <VTextField
              v-model="form.minimalNightStay"
              v-validate="schema.minimalNightStay"
              data-vv-name="minimal los"
              label="Minimal LOS"
              :error-messages="errors.collect('minimal los')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Maximal LOS">
            <VTextField
              v-model="form.maximalNightStay"
              v-validate="schema.maximalNightStay"
              data-vv-name="maximal los"
              label="Maximal LOS"
              :error-messages="errors.collect('maximal los')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Minimal day in advance">
            <VTextField
              v-model="form.minimalAdvancePurcase"
              v-validate="schema.minimalAdvancePurcase"
              data-vv-name="minimal day in advance"
              label="Minimal day in advance"
              :error-messages="errors.collect('minimal day in advance')"
              :color="color"
            />
          </BaseControlInput>

          <BaseControlInput label="Maximal day in advance">
            <VTextField
              v-model="form.maximalAdvancePurcase"
              v-validate="schema.maximalAdvancePurcase"
              data-vv-name="maximal day in advance"
              label="Maximal day in advance"
              :error-messages="errors.collect('maximal day in advance')"
              :color="color"
            />
          </BaseControlInput>
        </VFlex>
        <!-- End of Restrictions -->

        <!-- Benefits -->
        <VFlex class="mb-4" xs12 sm6>
          <div class="title mb-2">Benefits</div>

          <VTextField
            v-model="benefit"
            class="pl-3"
            label="Benefit name"
            append-outer-icon="add"
            :color="color"
            @click:append-outer="addBenefit"
          />

          <VTextField
            v-for="(value, index) in form.benefits"
            :key="index"
            v-model="form.benefits[index]"
            :color="color"
            class="pl-3"
            append-outer-icon="delete"
            hide-details
            @click:append-outer="removeBenefit(index)"
          />
        </VFlex>
        <!-- End of Benefits -->
      </VLayout>

      <template slot="card-actions-right">
        <VBtn depressed :disabled="loading" :to="{ name: 'rate-plan' }">
          Cancel
        </VBtn>

        <VBtn
          :color="color"
          :loading="loading"
          :disabled="!!errors.count()"
          type="submit"
          depressed
          :dark="!errors.count()"
        >
          {{ text.submit }}
        </VBtn>
      </template>
    </BaseView>
  </VForm>
</template>
